/**
 * App-side façade over the `track.js` embed script (`src/gnothi/web/src/track-script/track.ts`,
 * served from this app's own origin at `/track.js`).
 *
 * Every emit goes through here rather than touching `window.gnothi` directly, for two reasons:
 *   1. the script is absent whenever it should be — non-prod stages have no site id, the file
 *      is a deploy-time-only build output, and an opted-out visitor gets a no-op API — so a
 *      caller must never branch on that, and
 *   2. these five keys are FROZEN (arc decision D26) and enumerated by a sibling query lane in
 *      a separate worktree. A variant spelling silently orphans a dashboard column, so the keys
 *      live in one `const` here and callers reference it by name.
 *
 * Nothing in this module throws: an analytics emit must never take down the payment submit or
 * the signup handler it is measuring. The script guards its own bodies too; this is the second
 * belt, covering the case where `window.gnothi` is something unexpected entirely.
 */

/** The complete gnothi CLIENT key set (D26). `page_view` is the protocol pageview, sent by the script. */
export const CLIENT_EVENTS = {
  signup: 'gnothi:funnel:signup',
  payView: 'gnothi:pay:view',
  payIntent: 'gnothi:pay:intent',
  seriesOpen: 'gnothi:funnel:series_open',
} as const

export type ClientEvent = (typeof CLIENT_EVENTS)[keyof typeof CLIENT_EVENTS]

/**
 * Emit one of the frozen client events.
 *
 * `properties` must be a CLOSED LOW-CARDINALITY ENUM, same rule as the server-side `track()`:
 * never free text, ids, paths, or anything a person typed.
 */
export function trackEvent(name: ClientEvent, properties?: Record<string, string | number | boolean>): void {
  try {
    window.gnothi?.event(name, properties)
  } catch {
    /* never break the caller */
  }
}

/** Record a navigation. The script owns the redaction, the deferral and the de-duplication. */
export function trackPageView(): void {
  try {
    window.gnothi?.pageView()
  } catch {
    /* never break the caller */
  }
}

/**
 * One-shot signup attribution. The landing route shape and referrer host it carries were
 * captured in the script's closure at page load and live in memory only — this call cannot
 * read or write storage, and a second call is a no-op.
 */
export function trackSignup(): void {
  try {
    window.gnothi?.signup()
  } catch {
    /* never break the caller */
  }
}
